import { Alert, Card, Divider, Grid, Stack, Tooltip } from '@mui/joy';
import bigNumMath from 'technical/bigNumMath.ts';
import { bignumber } from 'mathjs';
import type { ReactElement } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import GFormProvider from 'components/technical/form/GFormProvider';
import gYupResolver from 'components/technical/form/gYupResolver';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar';
import Help from 'components/technical/Help/Help';
import { FormInput } from 'components/technical/inputs';
import GButton from 'components/technical/inputs/GButton/GButton';

import { type PortfolioBuilderInputFields, schema } from './PortfolioBuilder.utils.ts';
import PortfolioBuilderAssetList from './PortfolioBuilderAssetList';
import { type IPortfolioBuilderInputQuery, usePortfolioBuilderInputSuspenseQuery } from '../../../generated/graphql';
import {
  aggregateSupportedAssetPositions,
  getPublicSpotPositionsWithValue,
} from '../../portfolio/account/SubAccountPositionsService.ts';
import { assetMinPercentageContribution } from '../ImportPortfolioButton';

import ConfirmationDialog from 'components/technical/form/dialog/ConfirmationDialog.tsx';
import { defaultRowSpacing } from 'components/StackSpacing.ts';
import { useReportAssetGroup, type UseReportAssetGroupResultLoaded } from 'components/UseReportAssetGroups.tsx';
import PositionsGrid from './PositionsGrid.tsx';
import { useImperativeModal } from '../../technical/ImperativeModal/UseImperativeModal.tsx';
import PositionsSunburst from './PositionsSunburst.tsx';
import type { GroupWithAssetId } from 'components/portfolio/dashboard/PositionAggregationsService.ts';
import CreatePortfolioBuilderButton from './CreatePortfolioBuilderButton.tsx';
import { isValidNumber } from 'components/number.utils.ts';
import type { NotVerifiedAssetWithId } from 'components/market/asset/AssetService.tsx';
import { Download } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import type { RebalancedDefaultPortfolioDefinitionActionsState } from '../lab/definition/PortfolioDefinitionActions.tsx';
import isNil from 'lodash/fp/isNil';

export const DEFAULT_PORTFOLIO_EQUITY = 100;

const importingFromPortfolioDefPrecision = 4;
const importPrecMultiplier = 10 ** importingFromPortfolioDefPrecision;

const getDefaultValues = ({
  portfolioDefinition,
  idToAsset,
}: {
  portfolioDefinition: RebalancedDefaultPortfolioDefinitionActionsState | undefined;
  idToAsset: Map<string, NotVerifiedAssetWithId & { symbol: string }>;
}): { defaultValues: PortfolioBuilderInputFields; nonExistingAssetSymbols: string[] } => {
  const portfolioEquity = DEFAULT_PORTFOLIO_EQUITY;
  const positions: Record<string, BuilderPortfolioPosition> = {};
  const compositionWithKnownAssets = (portfolioDefinition?.composition ?? []).filter((comp) =>
    idToAsset.has(comp.assetId)
  );

  let totalWeight = 0;
  for (let i = 0; i < compositionWithKnownAssets.length; i++) {
    const position = compositionWithKnownAssets[i];
    const asset = idToAsset.get(position.assetId)!;
    let roundedWeight = Math.round(importPrecMultiplier * position.weight) / importPrecMultiplier;
    if (i === compositionWithKnownAssets.length - 1) {
      roundedWeight = 1 - totalWeight;
    } else {
      totalWeight += roundedWeight;
    }

    positions[position.assetId] = {
      asset,
      weight: roundedWeight.toFixed(importingFromPortfolioDefPrecision),
      value: (DEFAULT_PORTFOLIO_EQUITY * roundedWeight).toFixed(importingFromPortfolioDefPrecision),
    };
  }

  return {
    defaultValues: { portfolioEquity: portfolioEquity.toString(), positions },
    nonExistingAssetSymbols: (portfolioDefinition?.composition ?? [])
      .filter((comp) => !idToAsset.has(comp.assetId))
      .map((comp) => comp.symbol),
  };
};

type PortfolioBuilderProps = {
  reportAssetGroup: UseReportAssetGroupResultLoaded;
  assetGroups: {
    genieGroups: GroupWithAssetId[];
    userGroups: GroupWithAssetId[];
  };
  assets: IPortfolioBuilderInputQuery['assets']['feature'];
  portfolioPositions: IPortfolioBuilderInputQuery['portfolio']['positions']['positions'];
  portfolioDefinition?: RebalancedDefaultPortfolioDefinitionActionsState;
};

export type BuilderPortfolioPosition = {
  asset: NotVerifiedAssetWithId & {
    symbol: string;
  };
  weight: string | null;
  value: string | null;
};

const PortfolioBuilder = ({
  assets,
  assetGroups,
  portfolioPositions,
  reportAssetGroup,
  portfolioDefinition,
}: PortfolioBuilderProps): ReactElement => {
  const idToAsset = new Map(assets.map((asset) => [asset.id, asset]));
  const { defaultValues, nonExistingAssetSymbols } = getDefaultValues({
    portfolioDefinition,
    idToAsset,
  });

  const noPortfolioMessage = portfolioPositions.length === 0 ? 'No positions found in portfolio' : undefined;

  const methods = useForm<PortfolioBuilderInputFields>({
    resolver: gYupResolver(schema),
    mode: 'onChange',
    defaultValues,
  });

  const { showModal } = useImperativeModal();

  const portfolioEquity = useWatch({
    name: 'portfolioEquity',
    control: methods.control,
  });

  const portfolioEquityNumber = isNil(portfolioEquity)
    ? DEFAULT_PORTFOLIO_EQUITY
    : bignumber(portfolioEquity).toNumber();
  const showDollarValues = portfolioEquityNumber !== 0;

  const assetById = new Map(assets.map((asset) => [asset.id, asset]));

  function importFromPortfolio(): void {
    const publicSpotPositions = getPublicSpotPositionsWithValue(portfolioPositions);
    const aggregatedPositions = aggregateSupportedAssetPositions(publicSpotPositions, assets);

    const equity = bigNumMath.sum(aggregatedPositions.map((asset) => asset.value.abs()));
    const minCashValue = equity.mul(assetMinPercentageContribution);

    const newPositions = Object.fromEntries(
      aggregatedPositions
        .filter((pos) => pos.value.abs().greaterThan(minCashValue))
        .map((pos) => [
          pos.id,
          {
            asset: assetById.get(pos.id)!,
            value: pos.value.abs().toDP(2).toString(),
            weight: pos.value.mul(100).div(equity).toDP(2).toString(),
          },
        ])
    );

    methods.setValue('portfolioEquity', equity.toDP(2).toString());
    methods.setValue('positions', newPositions, { shouldValidate: true });
  }

  const handleEquityOnChange = (newEquityValue: string | null): void => {
    if (newEquityValue === null) {
      return;
    }

    const equityNumber = Number.parseFloat(newEquityValue);
    const positions = methods.getValues('positions');

    // update each position's value and weight based on the new equity value
    const updatedPositions = Object.fromEntries(
      Object.entries(positions).map(([id, pos]) => [
        id,
        {
          ...pos,
          value:
            isValidNumber(pos.weight) && isValidNumber(equityNumber)
              ? bignumber(pos.weight).div(100).mul(equityNumber).toDP(2).toString()
              : '0',
          weight: pos.weight,
        },
      ])
    );

    methods.setValue('positions', updatedPositions);
  };

  const positions = useWatch({
    control: methods.control,
    name: 'positions',
  });

  return (
    <Stack spacing={defaultRowSpacing}>
      <GFormProvider {...methods}>
        <HeaderBar title="Portfolio builder">
          <Stack gap={defaultRowSpacing} direction="row">
            <Stack direction="column" spacing={1} justifyContent="center" alignItems="center">
              {Object.keys(positions).length > 0 ? (
                <GButton
                  variant="outlined"
                  width="smaller"
                  onClick={() => {
                    methods.setValue('positions', {});
                    methods.setValue('portfolioEquity', null);
                  }}
                >
                  Clear All
                </GButton>
              ) : (
                <Tooltip title={noPortfolioMessage}>
                  {/* span needed so tooltip is shown even when button is disabled */}
                  <span>
                    <GButton
                      variant="solid"
                      width="normal"
                      disabled={portfolioPositions.length === 0}
                      onClick={(): void => {
                        if (methods.formState.isDirty) {
                          showModal(({ onClose }) => (
                            <ConfirmationDialog
                              onClose={onClose}
                              onApprove={async () => {
                                onClose();
                                importFromPortfolio();
                              }}
                            >
                              Operation will override current portfolio composition. Are you sure you want to proceed?
                            </ConfirmationDialog>
                          ));
                        } else {
                          importFromPortfolio();
                        }
                      }}
                      startDecorator={<Download />}
                    >
                      Import from portfolio
                    </GButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
            {Object.keys(positions).length > 0 && (
              <CreatePortfolioBuilderButton portfolioDefinition={portfolioDefinition} />
            )}
          </Stack>
        </HeaderBar>
        {nonExistingAssetSymbols.length > 0 && (
          <Alert color="warning">
            Some assets ({nonExistingAssetSymbols.join(' ')}) in the portfolio definition are not supported by portfolio
            builder. They will be removed from portfolio.
          </Alert>
        )}
        <form>
          <Stack direction="row" spacing={defaultRowSpacing}>
            <FormInput<PortfolioBuilderInputFields>
              showLabelAboveInput
              startDecorator="$"
              name="portfolioEquity"
              type="number"
              width="normal"
              label={
                <>
                  Portfolio equity&nbsp;
                  <Help>Unleveraged amount of funds in the portfolio</Help>
                </>
              }
              onChange={(newValue) => handleEquityOnChange(newValue)}
            />
            <PortfolioBuilderAssetList />
          </Stack>

          <Card
            sx={{
              marginTop: defaultRowSpacing,
            }}
          >
            <Grid container spacing={4}>
              <Grid xs={12} sm={12} md={9} gap={8} minHeight="400px">
                <PositionsGrid reportAssetGroup={reportAssetGroup} showValue={showDollarValues} />
              </Grid>
              <Divider
                sx={{ height: '90%', marginY: 'auto', display: { xs: 'none', sm: 'block' } }}
                orientation="vertical"
              />
              <Grid
                sm={12}
                md={3}
                sx={{
                  height: '90%',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <PositionsSunburst portfolioEquity={portfolioEquity} assetGroups={assetGroups} />
              </Grid>
            </Grid>
          </Card>
        </form>
      </GFormProvider>
    </Stack>
  );
};

const PortfolioBuilderContainer = (): ReactElement => {
  const location = useLocation();
  const routerState = location.state as RebalancedDefaultPortfolioDefinitionActionsState | undefined;
  const { data: inputData } = usePortfolioBuilderInputSuspenseQuery();
  const reportAssetGroup = useReportAssetGroup();

  if (!reportAssetGroup.loaded) {
    return <reportAssetGroup.Fallback />;
  }

  return (
    <PortfolioBuilder
      assetGroups={inputData.assets.assetGroups}
      portfolioPositions={inputData.portfolio.positions.positions}
      assets={inputData.assets.feature}
      reportAssetGroup={reportAssetGroup}
      portfolioDefinition={routerState}
    />
  );
};

export default PortfolioBuilderContainer;
